import React from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * Renders an ordered list of rich content blocks above a question input.
 *
 * Supported block shapes:
 *   { type: 'markdown', markdown: '...' }
 *   { type: 'html', html: '...' }                  // admin-authored only
 *   { type: 'image', src: '...', alt: '...', caption?: '...' }
 *   { type: 'video', src: '...', title?: '...' }
 *   { type: 'video_embed', src: '...', title?: '...' }
 *
 * Unknown types render nothing (forward-compatible). Falsy / empty arrays
 * also render nothing so callers can pass `question.content_blocks`
 * directly without guarding.
 */
export default function ContentBlocks({ blocks, className }) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return null;
  }

  return (
    <div className={['content-blocks', className].filter(Boolean).join(' ')}>
      {blocks.map((block, idx) => {
        const key = `${block.type || 'unknown'}-${idx}`;
        switch (block.type) {
          case 'markdown':
            return (
              <div key={key} className="content-blocks__markdown">
                <ReactMarkdown>{block.markdown || ''}</ReactMarkdown>
              </div>
            );
          case 'html':
            // Admin-authored content only; untrusted HTML must be sanitized
            // upstream before being persisted on Question.content_blocks.
            return (
              <div
                key={key}
                className="content-blocks__html"
                dangerouslySetInnerHTML={{ __html: block.html || '' }}
              />
            );
          case 'image':
            return (
              <figure key={key} className="content-blocks__image">
                <img
                  src={block.src}
                  alt={block.alt || ''}
                  className="content-blocks__image-img"
                />
                {block.caption && (
                  <figcaption className="content-blocks__image-caption">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'video_embed':
            return (
              <div key={key} className="content-blocks__video">
                <iframe
                  src={block.src}
                  title={block.title || 'Embedded video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            );
          case 'video':
            return (
              <figure key={key} className="content-blocks__video">
                <video controls src={block.src} title={block.title || undefined} />
                {block.caption && (
                  <figcaption className="content-blocks__video-caption">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
